<template>
  <div class="MainDiv" :class="{ 'App-Transitions': isTransitions }">
    <div v-if="!restrictedShop" class="pa-3">
      <v-alert class="mt-3" type="error">{{ error.network }}</v-alert>
    </div>
    <div v-else-if="!restrictedShop.country" class="d-flex justify-center align-center pa-3">
      <Loader />
    </div>
    <template v-else-if="restrictedShop.enabled">
      <ValidationObserver ref="form">
        <form @submit.prevent="handleSubmit">
          <div v-if="restrictedShop" class="px-12 pt-8 CreateEvent">
            <v-toolbar height="73px" class="CreateAppointment-header mb-5" flat>
              <h1>{{ $t('createAppointmentPage.title') }}</h1>
            </v-toolbar>

            <div class="mb-5 CreateEvent-inline">
              <div>
                <v-card class="CreateAppointment-card pa-5" flat>
                  <ValidationProvider name="service" rules="required" v-slot="{ errors }">
                    <v-select
                      v-model="selectedService"
                      background-color="secondary lighten-1"
                      class="ma-2 Calendar-select"
                      dense
                      :error="errors.length > 0"
                      height="55px"
                      hide-details
                      flat
                      light
                      :items="services"
                      item-text="displayName"
                      item-value="serviceUuid"
                      :no-data-text="$t('createAppointmentPage.dropdownService.noData')"
                      :placeholder="$t('createAppointmentPage.servicePlaceholder')"
                      solo
                    />
                  </ValidationProvider>
                </v-card>

                <v-card class="CreateAppointment-card pa-5 mt-5" flat>
                  <h4 class="CreateAppointment-card-title">
                    {{ $t('createAppointmentPage.sectionsLabels.contactInformations') }}
                  </h4>
                  <TextFieldValidation
                    v-for="(input, i) in Object.values(form)"
                    :key="i"
                    v-model="input.value"
                    :name="input.name"
                    :rules="input.rules"
                    :label="input.label"
                  />
                </v-card>

                <v-card class="CreateAppointment-card pa-5 mt-5" flat>
                  <h4 class="CreateAppointment-card-title">
                    {{ $t('createAppointmentPage.sectionsLabels.personaType') }}
                  </h4>
                  <ValidationProvider name="Persona Type" rules="required" v-slot="{ errors }">
                    <v-select
                      v-model="personaType"
                      :placeholder="$t('createAppointmentPage.form.personaType')"
                      :items="personaTypeOptions"
                      item-text="displayName"
                      item-value="value"
                      background-color="secondary lighten-1"
                      class="ma-2 mb-5 Calendar-select"
                      height="55px"
                      dense
                      flat
                      hide-details
                      light
                      solo
                      :error="errors.length > 0"
                    />
                    <p v-for="(error, index) in errors" :key="index" class="CreateAppointment-error">{{ error }}</p>
                  </ValidationProvider>
                  <ValidationProvider :name="$t('createAppointmentPage.form.age')" rules="required" v-slot="{ errors }">
                    <v-select
                      v-model="age"
                      :placeholder="$t('createAppointmentPage.form.age')"
                      :items="ageOptions"
                      background-color="secondary lighten-1"
                      class="ma-2 Calendar-select"
                      height="55px"
                      dense
                      flat
                      hide-details
                      light
                      solo
                    />
                    <p v-for="(error, index) in errors" :key="index" class="CreateAppointment-error">{{ error }}</p>
                  </ValidationProvider>
                </v-card>
              </div>

              <v-card class="CreateAppointment-card CreateAppointment-card-selectHours pa-5" flat>
                <h4 class="CreateAppointment-card-title">
                  {{ $t('createAppointmentPage.sectionsLabels.datePicker') }}
                </h4>
                <v-row justify="center">
                  <v-date-picker v-model="picker" :color="isTransitions ? 'transitionsPrimary' : 'primaryAccent'" />
                </v-row>
                <v-row justify="center">
                  <div class="CreateAppointment-hours ma-5">
                    <SelectHour
                      v-if="selectedService"
                      @selectHour="selectHour"
                      :currentDay="currentDay"
                      :hours="availablesHours"
                      :increment="serviceIncrement"
                      :openings="serviceOpenings"
                      :color="isTransitions ? 'transitionsPrimary' : 'primaryCalendar'"
                    />
                    <p v-else class="text-center">
                      {{ $t('createAppointmentPage.selectService') }}
                    </p>
                    <p v-for="(error, index) in formErrors" :key="index" class="CreateAppointment-error">
                      {{ error }}
                    </p>
                    <p class="CreateAppointment-error">{{ error.createAppointment || error.availabilities }}</p>
                  </div>
                </v-row>
              </v-card>
            </div>

            <v-row justify="end">
              <ValidationProvider name="CGU" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                <v-checkbox
                  v-model="acceptTerms"
                  :color="isTransitions ? 'transitionsPrimary' : 'primary'"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    <div>
                      <span class="mr-1">{{
                        isTransitions
                          ? $t('createAppointmentPage.cguLabel.transitionsText')
                          : $t('createAppointmentPage.cguLabel.text')
                      }}</span>
                      <a @click="visitCgu">
                        <span class="cguLink">{{
                          isTransitions
                            ? $t('createAppointmentPage.cguLabel.transitionsTextLink')
                            : $t('createAppointmentPage.cguLabel.textLink')
                        }}</span>
                      </a>
                    </div>
                  </template>
                </v-checkbox>
              </ValidationProvider>
            </v-row>
            <div v-if="restrictedShop.enabled" class="text-right">
              <v-btn
                class="d-inline-block px-12 submitButton"
                :color="isTransitions ? 'transitionsPrimary' : 'primary'"
                dark
                depressed
                large
                type="submit"
                >{{ $t('createAppointmentPage.submitButton') }}</v-btn
              >
              <v-snackbar v-model="snackbar" color="success" right top :timeout="6000">
                {{ $t('createAppointmentPage.appointmentConfirmation') }}
                <v-btn dark icon @click="snackbar = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-snackbar>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </template>
    <div v-else class="CreateAppointment-warning">
      {{ $t('createAppointmentPage.disabled') }}
    </div>
  </div>
</template>

<script>
import { localize, ValidationObserver, ValidationProvider } from 'vee-validate'
import gql from 'graphql-tag'
import { mapGetters } from 'vuex'

import TextFieldValidation from '@/components/lib/inputValidation/textFieldValidation'
import SelectHour from '@/components/ui/select/SelectHour'
import Loader from '@/components/lib/Loader'
import { appointmentAgeRanges } from '@/const/shared'
import { initVueGtm } from '@/services/gtm'

const GET_AVAILABILITIES = gql`
  query availabilities($beginDate: Date!, $endDate: Date!, $serviceUuid: GUID!) {
    availabilities(beginDate: $beginDate, endDate: $endDate, serviceUuid: $serviceUuid) {
      start
      end
    }
  }
`

const GET_SERVICES = key => gql`
  query restrictedShop($lang: String, $param: ${key === 'shopUuid' ? 'GUID' : 'ID'}) {
    restrictedShop(${key}: $param) {
      country
      ecpId
      enabled
      language
      locale
      ownerUuid
      services {
        duration
        enabled
        name(lang: $lang)
        serviceType
        serviceUuid
        openings {
          beginHour
          endHour
          dayOfWeek
        }
      }
      shopUuid
    }
  }
`

const CREATE_APPOINTMENT = gql`
  mutation createRestrictedAppointment($serviceUuid: GUID!, $appointmentInput: RestrictedCreateAppointmentInput!) {
    createRestrictedAppointment(serviceUuid: $serviceUuid, appointmentInput: $appointmentInput)
  }
`
export default {
  name: 'CreateAppointment',

  components: {
    Loader,
    SelectHour,
    TextFieldValidation,
    ValidationObserver,
    ValidationProvider
  },

  props: {
    eId: {
      type: String,
      required: false
    },
    landing: {
      type: Boolean,
      default: false
    },
    origin: {
      default: null,
      type: String,
      required: false
    },
    shopUuid: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      acceptTerms: false,
      age: '',
      availabilities: [],
      error: {
        availabilities: '',
        network: '',
        createAppointment: ''
      },
      form: {},
      formErrors: [],
      intervalSelected: [],
      personaType: '',
      picker: new Date().toISOString().substr(0, 10),
      restrictedShop: {},
      selectedHour: '',
      selectedService: '',
      snackbar: false,
      lang: {}
    }
  },
  computed: {
    ...mapGetters(['currentShop']),
    availablesHours() {
      return this.dailyAvailibilities.map(range => [range.start, range.end])
    },
    contentGroupName() {
      if (this.isTransitions) {
        return 'Transistions domain'
      }
      if (
        document.location.ancestorOrigins &&
        document.location.ancestorOrigins[0] &&
        document.location.ancestorOrigins[0].includes('essilor')
      ) {
        return 'Essilor domains'
      }

      return this.landing ? 'Essilor landings pages' : 'Practitioners websites'
    },
    dailyAvailibilities() {
      return !this.availabilities
        ? []
        : this.availabilities.filter(availability => {
            const availabilityDay = new Date(availability.start)
            const currentDay = this.currentDay
            return (
              availabilityDay.getFullYear() === currentDay.getFullYear() &&
              availabilityDay.getMonth() === currentDay.getMonth() &&
              availabilityDay.getDate() === currentDay.getDate()
            )
          })
    },
    currentDay() {
      return new Date(this.picker)
    },
    firstMonthDay() {
      const date = new Date(this.picker)
      return new Date(date.getFullYear(), date.getMonth(), 1)
    },
    isTransitions() {
      return this.origin === this.$const.origins.TRANSITIONS
      // transitions: this.$route.params.origin === this.$const.sites.transitions
    },
    lastMonthDay() {
      const date = new Date(this.picker)
      return new Date(date.getFullYear(), date.getMonth() + 1, 0)
    },
    ageOptions() {
      if (this.lang.country === 'SK') {
        return ['18-34', '35-45', '46-59', '60+']
      }
      return ['0-10', '11-17', '18-34', '35-45', '46-59', '60+']
    },
    personaTypeOptions() {
      if (
        this.lang.country === 'HU' ||
        this.lang.country === 'CZ' ||
        this.lang.country === 'SK' ||
        this.lang.country === 'RO'
      ) {
        return [
          {
            value: this.$const.personaType.FOR_ME,
            displayName: this.$t('createAppointmentPage.personaType.forMe')
          },
          {
            value: this.$const.personaType.FOR_SOMEONE_I_KNOW,
            displayName: this.$t('createAppointmentPage.personaType.forSomeoneIKnow')
          }
        ]
      }
      return[
          {
            value: this.$const.personaType.FOR_ME,
            displayName: this.$t('createAppointmentPage.personaType.forMe')
          },
          {
            value: this.$const.personaType.FOR_MY_CHILD,
            displayName: this.$t('createAppointmentPage.personaType.forMyChild')
          },
          {
            value: this.$const.personaType.FOR_SOMEONE_I_KNOW,
            displayName: this.$t('createAppointmentPage.personaType.forSomeoneIKnow')
          }
        ]
    },
    serviceIncrement() {
      return this.services.find(service => service.serviceUuid === this.selectedService).duration / 60
    },
    serviceOpenings() {
      return this.services.find(service => service.serviceUuid === this.selectedService).openings
    },
    services() {
      return this.restrictedShop.services
        .filter(service => service.enabled)
        .map(service => ({
          displayName: service.name,
          ...service
        }))
    }
  },

  apollo: {
    availabilities: {
      query: GET_AVAILABILITIES,
      variables() {
        return {
          serviceUuid: this.selectedService,
          beginDate: this.firstMonthDay.toISOString(),
          endDate: this.lastMonthDay.toISOString()
        }
      },
      skip() {
        return !this.selectedService || (!this.shopUuid && !this.eId)
      },
      error() {
        this.error.availabilities = this.$t('createAppointmentPage.error.network')
      }
    },
    restrictedShop: {
      query() {
        return GET_SERVICES(this && this.eId ? 'eId' : 'shopUuid')
      },
      variables() {
        return {
          lang: this.$vuetify.lang.current,
          param: this.eId || this.shopUuid
        }
      },
      skip() {
        return !this.shopUuid && !this.eId
      },
      result({ data: { restrictedShop } }) {
        if (
          restrictedShop &&
          restrictedShop.country &&
          this.$const.siteData[this.$site].countries[restrictedShop.country]
        ) {
          const lang =
            this.$const.languages.find(lang => lang.locale === restrictedShop.locale) || this.$const.languageFallback
          this.lang = lang
          if (lang) {
            this.$i18n.locale = lang.locale
            this.$vuetify.lang.current = lang.vuetify
            localize(lang.veeValidate, require(`vee-validate/dist/locale/${lang.veeValidate}.json`))
          }

          this.resetForm()

          initVueGtm(restrictedShop.country)

          this.$gtm.trackEvent({
            practitioner_id: restrictedShop.ecpId,
            shopUuid: restrictedShop.shopUuid,
            ContentGroup: this.contentGroupName,
            country: restrictedShop.country,
            language: this.$const.siteData[this.$site].countries[restrictedShop.country].defaultLanguage.slice(0, 2)
          })
        }
      },
      error() {
        this.error.network = this.$t('createAppointmentPage.error.network')
      }
    }
  },

  methods: {
    async handleSubmit() {
      const success = await this.$refs.form.validate()
      if (!success) {
        return
      }

      await this.submit()
      this.resetForm()
      this.$refs.form.reset()
    },
    initForm() {
      return {
        customerFirstName: {
          label: this.$t('createAppointmentPage.form.firstname'),
          name: this.$t('createAppointmentPage.form.firstname'),
          rules: 'required',
          value: ''
        },
        customerLastName: {
          label: this.$t('createAppointmentPage.form.lastname'),
          name: this.$t('createAppointmentPage.form.lastname'),
          rules: 'required',
          value: ''
        },
        customerPhoneNumber: {
          label: this.$t('createAppointmentPage.form.phone'),
          name: this.$t('createAppointmentPage.form.phone'),
          rules: 'required',
          value: ''
        },
        customerEmail: {
          label: this.$t('createAppointmentPage.form.email'),
          name: this.$t('createAppointmentPage.form.email'),
          rules: 'required|email',
          value: ''
        }
      }
    },
    selectHour(hour) {
      this.formErrors = []
      this.selectedHour = hour
    },
    resetForm() {
      this.acceptTerms = false
      this.personaType = ''
      this.selectedService = ''
      this.age = ''
      this.form = this.initForm()
    },
    sendGtmAddAppointment(appointmentId) {
      this.$gtm.trackEvent({
        appointment_id: appointmentId,
        booking_source: this.contentGroupName,
        event: 'AddAppointment',
        practitioner_id: this.restrictedShop.ecpId,
        shopUuid: this.shopUuid
      })
    },
    sendGtmToParent(appointmentId) {
      for (const o of [window.opener, window.parent].filter(Boolean)) {
        o.postMessage(
          JSON.stringify({
            appointment_id: appointmentId,
            booking_source: 'calendar-iframe',
            event: 'Form_Submit_Iframe',
            practitioner_id: this.restrictedShop.ecpId,
            service: (this.services.find(s => s.serviceUuid === this.selectedService) || {}).serviceType,
            time: this.selectedHour
          }),
          '*'
        )
      }
    },
    async submit() {
      this.formErrors = []
      if (!this.selectedHour) {
        this.formErrors.push(this.$t('createAppointmentPage.noPickedDate'))
        return
      }
      const appointmentInput = {
        age: appointmentAgeRanges[this.age],
        beginDateTime: this.selectedHour,
        customerEmail: this.form.customerEmail.value,
        customerFirstName: this.form.customerFirstName.value,
        customerLastName: this.form.customerLastName.value,
        customerPhoneNumber: this.form.customerPhoneNumber.value,
        origin: this.origin || this.landing ? this.$const.origins.LANDING : this.$const.origins.IFRAME,
        personaType: this.personaType
      }
      try {
        const { data } = await this.$apollo.mutate({
          mutation: CREATE_APPOINTMENT,
          variables: {
            serviceUuid: this.selectedService,
            appointmentInput
          }
        })

        this.sendGtmToParent(data.createRestrictedAppointment)
        this.sendGtmAddAppointment(data.createRestrictedAppointment)
        await this.$apollo.queries.availabilities.refetch()
        this.snackbar = true
      } catch (error) {
        console.error(error.message)
        this.error.createAppointment = this.$t('createAppointmentPage.error.createAppointment')
      }
    },
    visitCgu() {
      window.open(
        this.$t(
          this.isTransitions ? 'createAppointmentPage.cguLabel.transitionsLink' : 'createAppointmentPage.cguLabel.link'
        )
      )
    }
  }
}
</script>

<style scoped lang="scss">
.MainDiv {
  background-color: #e4e8ec;
}

.CreateEvent {
  max-width: 1200px;
  margin: auto;
}

.CreateAppointment-header {
  & h1 {
    font-size: 18px;
    font-weight: bold;
  }

  & .v-input {
    max-width: 420px;
  }
}

.CreateEvent-inline {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;

  @media (max-width: 1000px) {
    & {
      grid-template-columns: 1fr;
      grid-gap: 0;
    }
    & > *:first-child {
      padding-bottom: 0 !important;
    }
    & > *:nth-child(2) {
      padding-top: 3px !important;
    }
  }
}

.CreateAppointment-error {
  font-size: 0.8rem;
  color: red;
}

.CreateAppointment-warning {
  text-align: center;
  font-size: 1.1em;
  margin-top: 50px;
}

.CreateAppointment-card-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  margin-bottom: 15px;
}

.CreateAppointment-card-selectHours {
  @media (max-width: 1000px) {
    margin-top: 20px;

    h4 {
      margin-top: 17px;
    }
  }
}

.App-Transitions {
  .submitButton {
    border-radius: 22px;
  }

  .cguLink {
    color: var(--v-transitionsPrimary-base);
  }
}

.App-nikon-it {
  .submitButton {
    background-color: var(--v-primaryBtn-base) !important;
    border-color: var(--v-primaryBtn-base) !important;
    font-weight: bold !important;
    color: var(--v-secondary-base) !important;
  }
  .cguLink {
    color: var(--v-secondary-base) !important;
  }
}
</style>
